import { useCallback, useEffect, useMemo, useRef, useState, Fragment as FragmentWithKey } from "react";
import { api, extractError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Alert, AlertDescription } from "../components/ui/alert";
import { ChevronRight, ChevronDown, RefreshCcw, Search, X, ClipboardCheck, Download, AlertTriangle, FileText, Copy } from "lucide-react";
import { toast } from "sonner";
import { AllocationEditor } from "../components/RepairAllocation";
import SyncFullBayDialog from "../components/SyncFullBayDialog";
import RejectInvoiceDialog, { ReverseRejectDialog } from "../components/RejectInvoiceDialog";
import { RepeatHistoryProvider, RepeatRowPill, RepeatCorrectionChip } from "../components/RepairHistory";

const STATUSES = [
  { value: "", label: "All" },
  { value: "unassigned", label: "Unassigned" },
  { value: "partial", label: "Partial" },
  { value: "assigned", label: "Assigned" },
  { value: "closed", label: "Closed" },
  { value: "rejected", label: "Rejected" },
];

const UNIT_TYPES = [
  { value: "", label: "All" },
  { value: "truck", label: "Truck" },
  { value: "trailer", label: "Trailer" },
];

const REPEATS = [
  { value: "",      label: "All" },
  { value: "true",  label: "Has repeats" },
  { value: "false", label: "No repeats" },
];

const WARRANTY = [
  { value: "",     label: "All" },
  { value: "true", label: "Warranty candidates" },
];

const FULLBAY_PAYMENT_STATUSES = [
  { value: "",         label: "All" },
  { value: "paid",     label: "Paid" },
  { value: "unpaid",   label: "Unpaid" },
  { value: "partial",  label: "Partial" },
];

const PAGE_SIZE = 25;

function statusBadge(status) {
  const map = {
    unassigned: "bg-amber-50 text-amber-700 hover:bg-amber-50",
    partial: "bg-sky-50 text-sky-700 hover:bg-sky-50",
    assigned: "bg-emerald-50 text-emerald-700 hover:bg-emerald-50",
    closed: "bg-slate-100 text-slate-600 hover:bg-slate-100",
    // Phase 1 Reject — destructive tone. Uppercase display comes from the
    // consumer's `.toUpperCase()`; keep the pill weight consistent with the
    // other status badges so the row layout doesn't shift.
    rejected: "bg-rose-100 text-rose-700 hover:bg-rose-100",
  };
  return map[status] || "bg-slate-100 text-slate-600";
}

// FullBay payment status pill.  Derives from `raw_payload.balance` vs `total`
// at sync time — see backend rollup.derive_fullbay_payment_status.
//   paid     → green
//   unpaid   → muted rose (payment overdue signal, not alarming)
//   partial  → amber
//   null/""  → hides entirely
function PaymentStatusPill({ value, invoiceNumber }) {
  if (!value) return null;
  const map = {
    paid:    { label: "Paid",    cls: "border-emerald-200 bg-emerald-50 text-emerald-700" },
    unpaid:  { label: "Unpaid",  cls: "border-rose-200 bg-rose-50 text-rose-700" },
    partial: { label: "Partial", cls: "border-amber-200 bg-amber-50 text-amber-700" },
  };
  const meta = map[value];
  if (!meta) return null;
  return (
    <span
      className={`inline-flex items-center rounded-full border px-1.5 py-0 text-[10px] font-semibold uppercase tracking-wide ${meta.cls}`}
      title={`FullBay payment: ${meta.label}`}
      data-testid={`fullbay-payment-${invoiceNumber}`}
    >
      {meta.label}
    </span>
  );
}

function UnitTypeBadge({ value, invoiceNumber, subType }) {
  const map = {
    truck:   { label: "Truck",   cls: "border-indigo-200 bg-indigo-50 text-indigo-700" },
    trailer: { label: "Trailer", cls: "border-amber-200 bg-amber-50 text-amber-700" },
    unknown: { label: "Unknown", cls: "border-slate-200 bg-slate-50 text-slate-500" },
  };
  const meta = map[value] || map.unknown;
  const title = subType ? `${meta.label} · ${subType}` : meta.label;
  return (
    <span
      className={`inline-flex items-center rounded-full border px-1.5 py-0 text-[10px] font-semibold uppercase tracking-wide ${meta.cls}`}
      title={title}
      data-testid={`repair-unit-type-${invoiceNumber}`}
    >
      {meta.label}
    </span>
  );
}

// Small pill for the current Alvys equipment status.
//   active         → green
//   inactive       → slate/grey
//   leased_out     → violet
//   in_maintenance → orange
//   any other      → slate + raw label capitalized
// Returns null when status is null/empty (UI hides the pill).
const EQUIPMENT_STATUS_STYLES = {
  active:         { label: "Active",     cls: "border-emerald-200 bg-emerald-50 text-emerald-700" },
  inactive:       { label: "Inactive",   cls: "border-slate-200 bg-slate-100 text-slate-600" },
  leased_out:     { label: "Leased Out", cls: "border-violet-200 bg-violet-50 text-violet-700" },
  in_maintenance: { label: "In Maint.",  cls: "border-orange-200 bg-orange-50 text-orange-700" },
};

function EquipmentStatusPill({ value, rawValue, invoiceNumber }) {
  if (!value) return null;
  const known = EQUIPMENT_STATUS_STYLES[value];
  const label = known
    ? known.label
    : (rawValue
        ? String(rawValue).charAt(0).toUpperCase() + String(rawValue).slice(1).toLowerCase()
        : value.replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase()));
  const cls = known ? known.cls : "border-slate-200 bg-slate-100 text-slate-600";
  return (
    <span
      className={`inline-flex items-center rounded-full border px-1.5 py-0 text-[10px] font-semibold uppercase tracking-wide ${cls}`}
      title={rawValue ? `Alvys status: ${rawValue}` : label}
      data-testid={`repair-equipment-status-${invoiceNumber}`}
    >
      {label}
    </span>
  );
}

function formatMileage(v) {
  if (v == null || v === "") return null;
  const n = Number(v);
  if (!isFinite(n) || n <= 0) return null;
  return `${Math.round(n).toLocaleString()} mi`;
}

// Short date for Samsara "as of" sub-label (YYYY-MM-DD)
function formatShortDate(v) {
  if (!v) return null;
  const s = String(v);
  return s.slice(0, 10);
}

// Sub-text under Samsara Mileage — "OBD · as of 2026-08-27" or
// "GPS est. · as of 2026-08-27". Source is always shown so the reading's
// provenance is unambiguous.
function _samsaraMileageHint(detail) {
  const src = detail.samsara_mileage_source;
  const label = src === "obd" ? "OBD" : src === "gps_estimated" ? "GPS est." : "";
  const when = formatShortDate(detail.samsara_mileage_reading_date);
  if (label && when) return `${label} · as of ${when}`;
  if (label) return label;
  if (when) return `as of ${when}`;
  return null;
}

function formatMoney(v) {
  if (v == null) return "—";
  const n = Number(v);
  if (!isFinite(n)) return "—";
  return n.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

// Compact MM/DD/YY HH:mm (24h) for reading timestamps — matches the
// operator convention already used elsewhere in this file.
function formatCompactDT(v) {
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const yy = String(d.getFullYear()).slice(-2);
  const hh = String(d.getHours()).padStart(2, "0");
  const mn = String(d.getMinutes()).padStart(2, "0");
  return `${mm}/${dd}/${yy} ${hh}:${mn}`;
}

// Detect "same calendar day" — collapse two-line mileage into a single
// line when service_date matches the current reading date.
function isSameLocalDay(a, b) {
  if (!a || !b) return false;
  const da = new Date(a), db = new Date(b);
  if (Number.isNaN(da.getTime()) || Number.isNaN(db.getTime())) return false;
  return da.getFullYear() === db.getFullYear()
      && da.getMonth() === db.getMonth()
      && da.getDate() === db.getDate();
}

// Freshness hint — "47 h before service" / "2 d after service" — shown
// under mileage/location cells when the reading is > 12 h from the
// service_date timestamp.
//
// Anchor prefers the FULL service_date timestamp (e.g. "2026-09-02
// 12:39:04") so the hint reflects "was this reading close to when the
// repair actually happened". Falls back to midnight UTC of the date
// component when service_date is date-only (legacy invoices that never
// carried a clock time). Returns null when the reading is within 12 h
// (data is fresh enough, timestamp self-explanatory) or when either
// input is missing/unparseable.
//
// NOTE: this is deliberately different from the backend picker anchor
// (midnight UTC of service_date). The picker uses midnight for
// historical robustness; the UI hint asks a subtly different question.
function formatServiceGap(readingDt, serviceDateStr) {
  if (!readingDt || !serviceDateStr) return null;
  const rd = new Date(readingDt);
  if (Number.isNaN(rd.getTime())) return null;
  const raw = String(serviceDateStr);
  // Accept "YYYY-MM-DD HH:MM:SS[.ffff]" and "YYYY-MM-DDTHH:MM:SS...". Any
  // trailing timezone offset is honoured; a naive timestamp is treated as
  // UTC to match how service_date is stored server-side.
  const isoLike = raw.replace(" ", "T");
  const hasTz = /[Zz]|[+-]\d{2}:?\d{2}$/.test(isoLike);
  let anchor = new Date(hasTz ? isoLike : `${isoLike}Z`);
  if (Number.isNaN(anchor.getTime())) {
    // Fall back to date-only midnight-UTC parsing.
    const dayPart = raw.slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dayPart)) return null;
    anchor = new Date(`${dayPart}T00:00:00Z`);
    if (Number.isNaN(anchor.getTime())) return null;
  } else {
    // Date-only strings parse fine but land on midnight UTC. Additionally,
    // some legacy rows carry "YYYY-MM-DD 00:00:00" (time-present but all
    // zeros — really date-only). Detect that and treat identically.
    const timePart = /T(\d{2}):(\d{2}):(\d{2})/.exec(isoLike);
    const allZeros = timePart && timePart[1] === "00"
                              && timePart[2] === "00"
                              && timePart[3] === "00";
    if (allZeros) {
      // Already at midnight — no change needed, keep the parsed value.
    }
  }
  const diffMs = rd.getTime() - anchor.getTime();
  const absH = Math.abs(diffMs) / 3_600_000;
  if (absH <= 12) return null;
  const direction = diffMs < 0 ? "before service" : "after service";
  if (absH >= 48) {
    const days = absH / 24;
    const shown = days >= 10 ? Math.round(days) : Math.round(days * 10) / 10;
    return `~${shown} d ${direction}`;
  }
  return `~${Math.round(absH)} h ${direction}`;
}

function formatDate(v) {
  if (!v) return "—";
  return String(v).slice(0, 10);
}

// "Aug 26" — compact suffix for the muted last-driver row cell.
function formatMMMD(v) {
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
}

// "Aug 26, 2026" — expanded detail meta sub-line.
function formatMMMDYYYY(v) {
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
}

// ---------------------------------------------------------------------------
// Driver typeahead (mini)
// ---------------------------------------------------------------------------
function DriverPicker({ value, name, onChange, testid = "repairs-filter-driver" }) {
  const [q, setQ] = useState(name || "");
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);

  useEffect(() => {
    if (!q || q.length < 2) { setResults([]); return; }
    let cancelled = false;
    const t = setTimeout(() => {
      api.get("/tms/drivers/search", { params: { q, limit: 8 } })
        .then((r) => { if (!cancelled) setResults(r.data || []); })
        .catch(() => setResults([]));
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q]);

  return (
    <div className="relative">
      <div className="flex items-center gap-1">
        <Input
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Filter by driver…"
          data-testid={testid}
          className="h-9"
        />
        {value && (
          <Button
            variant="ghost" size="sm"
            onClick={() => { setQ(""); onChange(null, null); }}
            data-testid={`${testid}-clear`}
          >
            <X className="h-3 w-3" />
          </Button>
        )}
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-10 mt-1 w-full max-h-48 overflow-auto rounded-md border border-slate-200 bg-white shadow">
          {results.map((r) => (
            <button
              key={r.id}
              className="block w-full px-3 py-1.5 text-left text-sm hover:bg-slate-50"
              onClick={() => { setQ(r.label); onChange(r.id, r.label); setOpen(false); }}
            >
              {r.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Row detail — in-app "receipt" view (Corrections editable for admin/accounting)
// ---------------------------------------------------------------------------
function InvoiceReceipt({ invoiceId, canEdit, isAdmin, onInvoiceChanged, invoiceMeta }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [samsaraRefreshing, setSamsaraRefreshing] = useState(false);
  const [confirmingAcceptSamsara, setConfirmingAcceptSamsara] = useState(false);
  const [acceptingSamsara, setAcceptingSamsara] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [reverseRejectOpen, setReverseRejectOpen] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    // Stale-while-revalidate: only show the `Loading…` gate on the FIRST
    // fetch (detail=== null). On subsequent reloads (chip clicks, refresh
    // Samsara, allocation changes, etc.) keep the last-good detail visible
    // while the refetch is in flight, then swap atomically. Without this,
    // every mutation unmounted the whole expanded panel via the `if (loading)`
    // early return, collapsing the ~800px receipt to a 25px "Loading…" div —
    // which the browser interprets as the page shrinking and pops the scroll
    // to the top of the invoice list.
    //
    // Errors still transition through `setErr` which re-mounts, but errors
    // are terminal and rare enough that the scroll jump on error is fine
    // (the user needs to see the error anyway).
    setDetail((prev) => {
      if (prev === null) setLoading(true);
      return prev;
    });
    setErr("");
    api.get(`/equipment-repairs/invoices/${invoiceId}`)
      .then((r) => { if (!cancelled) setDetail(r.data); })
      .catch((e) => { if (!cancelled) setErr(extractError(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [invoiceId, reloadTick]);

  // Cross-component reload trigger — drawer's warranty quick-action fires
  // this after PUT /allocations so the receipt shows the pre-filled row.
  useEffect(() => {
    const onReload = (e) => {
      if (e.detail?.invoiceId && e.detail.invoiceId !== invoiceId) return;
      setReloadTick((n) => n + 1);
      onInvoiceChanged?.();
    };
    window.addEventListener("repairs:invoice-reload", onReload);
    return () => window.removeEventListener("repairs:invoice-reload", onReload);
  }, [invoiceId, onInvoiceChanged]);

  const reload = () => setReloadTick((n) => n + 1);

  if (loading && !detail) return <div className="p-4 text-sm text-slate-500">Loading…</div>;
  if (err) return <Alert variant="destructive" className="m-2"><AlertDescription>{err}</AlertDescription></Alert>;
  if (!detail) return null;

  const so = detail.raw_payload?.ServiceOrder || {};
  const shopTitle = detail.raw_payload?.shopTitle;
  const complaints = so.Complaints || [];

  // Map stored corrections by (complaint_index, correction_index)
  const corrByKey = new Map();
  (detail.corrections || []).forEach((c) => corrByKey.set(`${c.complaint_index}:${c.correction_index}`, c));

  const eligibleForCommit = (detail.corrections || []).some((c) => c.status === "assigned");
  const commitDisabled = !eligibleForCommit || committing;

  const commitInvoice = async () => {
    setCommitting(true);
    try {
      const { data } = await api.post(`/equipment-repairs/invoices/${detail.id}/commit-all`, {}, { timeout: 60000 });
      const bits = [
        `${data.expenses_created} expenses`,
        `${data.alvys_synced} Alvys synced`,
      ];
      if (data.alvys_failed) bits.push(`${data.alvys_failed} sync failed`);
      toast.success(`Invoice committed · ${bits.join(" · ")}`);
      reload();
      onInvoiceChanged?.();
      window.dispatchEvent(new Event("repairs:refresh-badge"));
    } catch (e) { toast.error(extractError(e)); }
    finally { setCommitting(false); }
  };

  // Admin-only "Refresh Samsara" — force-refetches mileage + driver for
  // this one invoice (bypasses backend idempotency guard). Debounced by
  // the `samsaraRefreshing` flag; the underlying Samsara call takes ~2s.
  const refreshSamsara = async () => {
    if (samsaraRefreshing) return;
    setSamsaraRefreshing(true);
    try {
      const { data } = await api.post(`/equipment-repairs/invoices/${detail.id}/samsara-refresh`);
      if (!data.refreshed) {
        toast.info(data.reason === "samsara_disabled_or_no_unit"
          ? "Samsara not configured or no unit on invoice"
          : "Samsara refresh: nothing to update");
      } else {
        const f = data.fields || {};
        const changes = [];
        if (f.samsara_driver_id !== undefined) changes.push("driver");
        if (f.samsara_mileage !== undefined) changes.push("mileage");
        if (f.samsara_vehicle_id !== undefined && changes.length === 0) changes.push("vehicle");
        toast.success(`Samsara refreshed: ${changes.length ? changes.join(", ") : "vehicle only"}`);
      }
      reload();
      onInvoiceChanged?.();
    } catch (e) { toast.error(extractError(e)); }
    finally { setSamsaraRefreshing(false); }
  };

  // Phase C — accountant accepts the Samsara driver over Alvys. Suggestion
  // level only; committed allocations keep their historical driver_id.
  const acceptSamsaraDriver = async () => {
    if (acceptingSamsara) return;
    setAcceptingSamsara(true);
    try {
      const { data } = await api.post(`/equipment-repairs/invoices/${detail.id}/accept-samsara-driver`);
      const newName = data?.fields?.suggested_driver_name || detail.samsara_driver_name || "Samsara driver";
      toast.success(`Suggested driver updated to ${newName}. Mismatch resolved.`);
      setConfirmingAcceptSamsara(false);
      reload();
      onInvoiceChanged?.();
      window.dispatchEvent(new CustomEvent("repairs:invoice-reload", { detail: { invoiceId: detail.id } }));
      window.dispatchEvent(new Event("repairs:refresh-badge"));
    } catch (e) { toast.error(extractError(e)); }
    finally { setAcceptingSamsara(false); }
  };

  const bulkAllocate = async (bucket) => {
    if (selected.size === 0) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post(`/equipment-repairs/invoices/${detail.id}/bulk-allocate`, {
        correction_ids: Array.from(selected), charge_type: bucket,
      });
      const noDriver = (data.results || []).filter((r) => r.error === "no_driver").length;
      if (noDriver > 0) {
        toast.warning(`${data.assigned}/${data.results.length} assigned · ${noDriver} skipped (no driver suggestion)`);
      } else {
        toast.success(`Assigned ${data.assigned} correction${data.assigned === 1 ? "" : "s"} to ${bucket}`);
      }
      setSelected(new Set());
      reload();
      window.dispatchEvent(new Event("repairs:refresh-badge"));
    } catch (e) { toast.error(extractError(e)); }
    finally { setBulkBusy(false); }
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  return (
    <div className="border-t border-slate-200 bg-slate-50 p-4" data-testid={`repairs-detail-${detail.invoice_number}`}>
      {/* Meta grid */}
      <div className="relative mb-4 rounded-md border border-slate-200 bg-white">
        {/* FullBay payment badge — anchored top-right of the card on md+ so it
            doesn't consume a grid cell. On mobile the same node flows inline
            above the grid so nothing collides with the 2-col mobile layout. */}
        <div
          className="flex flex-wrap items-baseline gap-2 border-b border-slate-100 px-4 py-2 md:absolute md:right-3 md:top-2 md:z-10 md:border-0 md:bg-transparent md:p-0"
          data-testid={`fullbay-payment-cell-${detail.invoice_number}`}
        >
          {detail.rollup_status === "rejected" && (
            <Badge
              className="bg-rose-100 text-rose-700 hover:bg-rose-100"
              data-testid={`rejected-pill-detail-${detail.invoice_number}`}
              title="Invoice rejected — a Shop Credit Claim was auto-created."
            >
              REJECTED
            </Badge>
          )}
          <div className="text-xs uppercase tracking-wide text-slate-500">FullBay Payment</div>
          <div className="text-sm text-slate-900">
            {detail.fullbay_payment_status
              ? <PaymentStatusPill value={detail.fullbay_payment_status} invoiceNumber={detail.invoice_number} />
              : <span className="text-slate-400">—</span>}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-x-8 gap-y-2 p-4 text-sm md:grid-cols-[minmax(140px,0.85fr)_minmax(280px,1.6fr)_minmax(180px,1fr)_minmax(180px,1fr)] md:pt-11">
          <MetaCell label="Shop" value={shopTitle} />
        <MetaCell label="Repair Order #" value={detail.repair_order_number} />
        <MetaCell label="Technician" value={detail.technician} />
        <MetaCell label="Service Date" value={String(detail.service_date || "").slice(0, 10)} />
        <div>
          <div className="text-xs uppercase tracking-wide text-slate-500">Unit</div>
          <div className="mt-0.5 flex items-center gap-1.5 text-sm text-slate-900">
            <span>{detail.unit_number || <span className="text-slate-400">—</span>}</span>
            <UnitTypeBadge value={detail.unit_type} invoiceNumber={detail.invoice_number} subType={detail.unit_subtype} />
            <EquipmentStatusPill
              value={detail.equipment_status}
              rawValue={detail.equipment_status_raw}
              invoiceNumber={detail.invoice_number}
            />
          </div>
          {detail.unit_subtype && <div className="text-xs text-slate-400">{detail.unit_subtype}</div>}
        </div>
        <MetaCell label="VIN" value={detail.vin} mono />
        <MetaCell
          label="Suggested Driver"
          value={
            detail.suggested_driver_name
              ? detail.suggested_driver_name
              : detail.last_alvys_driver_name
                ? <span className="text-slate-400">— (No driver assigned on service date)</span>
                : null
          }
          hint={
            !detail.suggested_driver_id && detail.last_alvys_driver_name
              ? `Last: ${detail.last_alvys_driver_name}${detail.driver_still_assigned ? " · still assigned" : ""}${formatMMMDYYYY(detail.last_alvys_driver_end_date) ? ` · last trip ${formatMMMDYYYY(detail.last_alvys_driver_end_date)}` : ""}`
              : detail.driver_resolution_reason ? `via ${detail.driver_resolution_reason}` : null
          }
          decorator={detail.driver_mismatch ? (
            <MismatchAsterisk
              tooltip={`Samsara reports different driver: ${detail.samsara_driver_name || "unknown"}`}
              testid={`driver-mismatch-alvys-${detail.invoice_number}`}
            />
          ) : null}
        />
        <MetaCell label="Total" value={formatMoney(detail.total)} />
        <MetaCell
          label="Chassis Mileage"
          value={formatMileage(detail.chassis_mileage)}
          decorator={detail.mileage_mismatch ? (
            <MismatchAsterisk
              tooltip={`Samsara reports different mileage: ${formatMileage(detail.samsara_mileage) || "unknown"}`}
              testid={`mileage-mismatch-fullbay-${detail.invoice_number}`}
            />
          ) : null}
        />
        <SamsaraMileageCell detail={detail} />
        <MetaCell
          label="Samsara Driver"
          value={
            detail.samsara_driver_name
              ? detail.samsara_driver_name
              : detail.last_samsara_driver_name
                ? <span className="text-slate-400">— (No driver assigned on service date)</span>
                : null
          }
          hint={
            !detail.samsara_driver_id && detail.last_samsara_driver_name
              ? `Last: ${detail.last_samsara_driver_name}${formatMMMDYYYY(detail.last_samsara_driver_end_date) ? ` · last trip ${formatMMMDYYYY(detail.last_samsara_driver_end_date)}` : ""}`
              : detail.samsara_driver_resolution_reason ? `via ${detail.samsara_driver_resolution_reason}` : null
          }
          decorator={
            <span className="inline-flex items-center gap-1" data-testid={`samsara-driver-${detail.invoice_number}`}>
              {detail.driver_mismatch && (
                <MismatchAsterisk
                  tooltip={`Alvys reports different driver: ${detail.suggested_driver_name || "unknown"}`}
                  testid={`driver-mismatch-samsara-${detail.invoice_number}`}
                />
              )}
            </span>
          }
        />
        <SamsaraLocationCell detail={detail} />
        </div>
        {(isAdmin || canEdit) && (
          <div
            className="flex flex-wrap items-center justify-end gap-2 border-t border-slate-100 px-4 py-2"
            data-testid={`meta-actions-${detail.invoice_number}`}
          >
            {isAdmin && (
              <Button
                size="sm"
                variant="outline"
                disabled={samsaraRefreshing}
                onClick={refreshSamsara}
                data-testid={`samsara-refresh-${detail.invoice_number}`}
                title="Force re-fetch Samsara mileage + driver for this invoice"
              >
                <RefreshCcw className={`mr-1 h-4 w-4 ${samsaraRefreshing ? "animate-spin" : ""}`} />
                {samsaraRefreshing ? "Refreshing…" : "Refresh Samsara"}
              </Button>
            )}
            {canEdit && detail.rollup_status !== "rejected" && (
              <Button
                size="sm"
                variant="outline"
                className="border-rose-300 text-rose-700 hover:bg-rose-50 hover:text-rose-800"
                onClick={() => setRejectOpen(true)}
                data-testid={`reject-invoice-btn-${detail.invoice_number}`}
                title="Reject this invoice and auto-create a Shop Credit claim"
              >
                <X className="mr-1 h-4 w-4" /> Reject Invoice
              </Button>
            )}
            {canEdit && detail.rollup_status === "rejected" && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setReverseRejectOpen(true)}
                data-testid={`reverse-reject-btn-${detail.invoice_number}`}
                title="Reverse the reject — restores corrections + voids draft claim"
              >
                <RefreshCcw className="mr-1 h-4 w-4" /> Reverse Reject
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Phase C — 1-click Samsara acceptance for admin+accounting when
          Alvys and Samsara disagree. Hidden after resolution (the flag
          flips false on the next detail reload). */}
      {canEdit && detail.driver_mismatch && (
        <div
          className="mb-3 flex flex-wrap items-center gap-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm"
          data-testid={`mismatch-bar-${detail.invoice_number}`}
        >
          <AlertTriangle className="h-4 w-4 shrink-0 text-rose-600" />
          {!confirmingAcceptSamsara ? (
            <>
              <span className="text-slate-700">
                Alvys says <strong>{detail.suggested_driver_name || "unknown"}</strong>, Samsara says{" "}
                <strong>{detail.samsara_driver_name || "unknown"}</strong>.
              </span>
              <div className="ml-auto flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setConfirmingAcceptSamsara(true)}
                  data-testid={`accept-samsara-driver-${detail.invoice_number}`}
                >
                  Use Samsara driver
                </Button>
              </div>
            </>
          ) : (
            <>
              <span className="text-slate-700">
                This will replace the suggested driver with{" "}
                <strong>{detail.samsara_driver_name || "unknown"}</strong>. Existing committed
                allocations are unchanged. Proceed?
              </span>
              <div className="ml-auto flex items-center gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={acceptingSamsara}
                  onClick={() => setConfirmingAcceptSamsara(false)}
                  data-testid={`accept-samsara-cancel-${detail.invoice_number}`}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  disabled={acceptingSamsara}
                  onClick={acceptSamsaraDriver}
                  className="bg-rose-600 text-white hover:bg-rose-700"
                  data-testid={`accept-samsara-confirm-${detail.invoice_number}`}
                >
                  {acceptingSamsara ? "Updating…" : "Confirm"}
                </Button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Alvys Notes — sits above the bulk toolbar / commit CTA so operators
          see the lessee/leasing context as a final read-check before committing.
          Visible to all roles that can view the invoice; refresh button is
          gated to admin/accounting via `canRefresh={canEdit}`. */}
      <AlvysNotesPanel
        invoiceId={detail.id}
        invoiceNumber={detail.invoice_number}
        notes={detail.alvys_notes || []}
        syncedAt={detail.alvys_notes_synced_at}
        canRefresh={canEdit}
        onRefreshed={(payload) => {
          setDetail((d) => ({
            ...d,
            alvys_notes: payload.alvys_notes || [],
            alvys_notes_synced_at: payload.alvys_notes_synced_at || null,
          }));
        }}
      />

      {/* Bulk toolbar + commit invoice */}
      {canEdit && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm" data-testid="bulk-toolbar">
          <span className="text-xs text-slate-500">
            {selected.size > 0 ? `${selected.size} selected` : "Select corrections to bulk-assign, or use the chips per row."}
          </span>
          {selected.size > 0 && (
            <>
              <Button size="sm" variant="outline" disabled={bulkBusy} onClick={() => bulkAllocate("warranty")} data-testid="bulk-btn-warranty">Warranty</Button>
              <Button size="sm" variant="outline" disabled={bulkBusy} onClick={() => bulkAllocate("company")} data-testid="bulk-btn-company">Company</Button>
              <Button size="sm" variant="outline" disabled={bulkBusy || !detail.suggested_driver_id}
                      title={detail.suggested_driver_id ? undefined : "No suggested driver — assign per row"}
                      onClick={() => bulkAllocate("driver")} data-testid="bulk-btn-driver">
                Driver (use suggested)
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())} data-testid="bulk-btn-clear">Clear</Button>
            </>
          )}
          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-slate-400">{detail.corrections.filter((c) => c.status === "assigned").length} ready to commit</span>
            <Button size="sm" disabled={commitDisabled} onClick={commitInvoice}
                    className="bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60"
                    data-testid={`commit-invoice-${detail.invoice_number}`}>
              <ClipboardCheck className="mr-1 h-4 w-4" />
              {committing ? "Committing…" : "Commit invoice"}
            </Button>
          </div>
        </div>
      )}

      {complaints.length === 0 && (
        <p className="text-sm text-slate-500">No complaints on this invoice.</p>
      )}

      {complaints.map((comp, c_idx) => (
        <div key={c_idx} className="mb-4 rounded-md border border-slate-200 bg-white">
          <div className="border-b border-slate-200 bg-slate-50 px-4 py-2">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Complaint #{c_idx + 1}
            </div>
            <div className="mt-0.5 whitespace-pre-wrap text-sm text-slate-800">{comp.note || <em className="text-slate-400">no note</em>}</div>
            {comp.cause && (
              <div className="mt-1 whitespace-pre-wrap text-xs text-slate-500"><span className="font-semibold">Cause:</span> {comp.cause}</div>
            )}
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                {canEdit && <TableHead className="w-8" />}
                <TableHead className="w-2/5">Correction</TableHead>
                <TableHead className="text-right">Labor</TableHead>
                <TableHead className="text-right">Parts</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead className="w-2/5">Allocation</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(comp.Corrections || []).map((corr, r_idx) => {
                const stored = corrByKey.get(`${c_idx}:${r_idx}`);
                const desc = corr.actualCorrection || corr.recommendedCorrection || corr.title || "—";
                const displayAmount = stored?.amount ?? ((Number(corr.laborTotal) || 0) + (Number(corr.partsTotal) || 0));
                return (
                  <TableRow key={r_idx} data-testid={stored ? `correction-row-${stored.id}` : undefined}>
                    {canEdit && (
                      <TableCell className="align-top">
                        {stored && stored.status !== "charged" && (
                          <Checkbox
                            checked={selected.has(stored.id)}
                            onCheckedChange={() => toggleSelect(stored.id)}
                            data-testid={`row-select-${stored.id}`}
                          />
                        )}
                      </TableCell>
                    )}
                    <TableCell className="align-top">
                      <div className="whitespace-pre-wrap text-sm text-slate-800">{desc}</div>
                      {(corr.globalComponent || corr.globalSystem) && (
                        <div className="mt-0.5 text-xs text-slate-500">
                          {corr.globalComponent}{corr.globalComponent && corr.globalSystem ? " · " : ""}{corr.globalSystem}
                        </div>
                      )}
                      {stored && (
                        <div className="mt-1">
                          <RepeatCorrectionChip
                            correctionId={stored.id}
                            correction={{
                              description: desc,
                              amount: displayAmount,
                              status: stored.status,
                              global_component: corr.globalComponent,
                              global_system: corr.globalSystem,
                              // Phase 4a — pass the resolution snapshot so
                              // RepairHistory drawer can render the
                              // "Reviewed" pill on re-open without a
                              // separate fetch.
                              repeat_resolution: stored.repeat_resolution,
                              repeat_resolved_at: stored.repeat_resolved_at,
                              repeat_resolved_by: stored.repeat_resolved_by,
                              repeat_dismiss_note: stored.repeat_dismiss_note,
                              repeat_prior_invoice_number: stored.repeat_prior_invoice_number,
                            }}
                            invoiceMeta={{
                              id: detail.id,
                              invoice_number: detail.invoice_number,
                              unit_number: detail.unit_number,
                              vin: detail.vin,
                            }}
                            parentHasRepeats={(invoiceMeta?.repeat_count || 0) > 0}
                          />
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-right align-top text-sm">{formatMoney(corr.laborTotal)}</TableCell>
                    <TableCell className="text-right align-top text-sm">{formatMoney(corr.partsTotal)}</TableCell>
                    <TableCell className="text-right align-top text-sm font-medium">{formatMoney(displayAmount)}</TableCell>
                    <TableCell className="align-top">
                      {stored ? (
                        <AllocationEditor
                          correction={stored}
                          invoice={detail}
                          readOnly={!canEdit}
                          onChange={(next) => {
                            // Optimistic: replace correction in local detail (keep raw + other fields)
                            setDetail((d) => ({
                              ...d,
                              corrections: d.corrections.map((c) =>
                                c.id === stored.id ? { ...c, ...next } : c
                              ),
                            }));
                            // Refresh from server to catch invoice-level rollup changes
                            reload();
                            onInvoiceChanged?.();
                          }}
                        />
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      ))}

      {canEdit && <AuditPanel invoiceId={detail.id} invoiceNumber={detail.invoice_number} reloadTick={reloadTick} />}

      {/* Phase 2 Reject — full-invoice reject + reverse dialogs. Both trigger
          a receipt reload + parent list refresh on success so the rollup
          badge, action button, and list-row pill stay consistent. */}
      {canEdit && (
        <>
          <RejectInvoiceDialog
            open={rejectOpen}
            onOpenChange={setRejectOpen}
            invoice={detail}
            onSuccess={() => {
              reload();
              onInvoiceChanged?.();
              window.dispatchEvent(new Event("repairs:refresh-badge"));
            }}
          />
          <ReverseRejectDialog
            open={reverseRejectOpen}
            onOpenChange={setReverseRejectOpen}
            invoice={detail}
            onSuccess={() => {
              reload();
              onInvoiceChanged?.();
              window.dispatchEvent(new Event("repairs:refresh-badge"));
            }}
          />
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// AlvysNotesPanel — Phase 2b. Collapsible mirror of Alvys unit `Notes[]`.
// Read-only for all roles that can see the invoice; refresh button gated
// to admin/accounting (`canRefresh` prop). Data is fetched on every sync
// and available via a manual refresh (accounting/admin only).
// ---------------------------------------------------------------------------
function AlvysNotesPanel({ invoiceId, invoiceNumber, notes, syncedAt, canRefresh, onRefreshed }) {
  const [open, setOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [flash, setFlash] = useState(false);

  const sorted = useMemo(() => {
    return [...(notes || [])].sort((a, b) => {
      const ta = a.time ? new Date(a.time).getTime() : 0;
      const tb = b.time ? new Date(b.time).getTime() : 0;
      return tb - ta;
    });
  }, [notes]);

  const freshness = useMemo(() => {
    if (!syncedAt) return { color: null, label: "Never synced" };
    const ageMs = Date.now() - new Date(syncedAt).getTime();
    const minutes = Math.floor(ageMs / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    let rel;
    if (minutes < 1) rel = "just now";
    else if (minutes < 60) rel = `${minutes} min ago`;
    else if (hours < 24) rel = `${hours}h ago`;
    else if (days < 30) rel = `${days}d ago`;
    else rel = `${Math.floor(days / 30)}mo ago`;
    let color = "grey";
    if (minutes < 5) color = "green";
    else if (days > 30) color = "yellow";
    return { color, label: rel };
  }, [syncedAt]);

  const doRefresh = async () => {
    setRefreshing(true);
    try {
      const r = await api.post(`/equipment-repairs/invoices/${invoiceId}/refresh-alvys-notes`);
      onRefreshed?.(r.data);
      setFlash(true);
      setTimeout(() => setFlash(false), 900);
      if (r.data?.warning === "unit_not_found_in_alvys") {
        toast.warning("Unit isn't resolvable in Alvys — nothing to show.");
      } else {
        toast.success("Alvys notes refreshed");
      }
    } catch (e) {
      const status = e?.response?.status;
      if (status === 429) {
        toast.error("Slow down — too many refreshes. Try again in a minute.");
      } else {
        toast.error("Couldn't reach Alvys; showing last synced values.");
      }
    } finally {
      setRefreshing(false);
    }
  };

  const dotClass =
    freshness.color === "green" ? "bg-emerald-500" :
    freshness.color === "yellow" ? "bg-amber-500" :
    freshness.color === "grey" ? "bg-slate-400" : "";

  return (
    <div
      className={`mb-4 rounded-md border ${flash ? "border-emerald-300 bg-emerald-50/50" : "border-slate-200 bg-white"} transition-colors`}
      data-testid={`alvys-notes-panel-${invoiceNumber}`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-slate-50"
        data-testid={`alvys-notes-toggle-${invoiceNumber}`}
      >
        <ChevronRight className={`h-4 w-4 text-slate-500 transition-transform ${open ? "rotate-90" : ""}`} />
        <FileText className="h-4 w-4 text-slate-500" />
        <span className="text-sm font-medium text-slate-800">Alvys Notes</span>
        <Badge
          variant="secondary"
          className="bg-slate-100 text-slate-700"
          data-testid={`alvys-notes-count-${invoiceNumber}`}
        >
          {sorted.length}
        </Badge>
        <div className="ml-auto flex items-center gap-2">
          <span
            className="flex items-center gap-1.5 text-xs text-slate-500"
            title={syncedAt ? new Date(syncedAt).toLocaleString() : "Never synced"}
            data-testid={`alvys-notes-freshness-${invoiceNumber}`}
          >
            {dotClass && <span className={`h-1.5 w-1.5 rounded-full ${dotClass}`} />}
            {freshness.label}
          </span>
          {canRefresh && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); doRefresh(); }}
              disabled={refreshing}
              className="rounded-md border border-slate-200 bg-white p-1.5 text-slate-500 hover:bg-slate-50 disabled:opacity-40"
              title="Refresh from Alvys"
              data-testid={`alvys-notes-refresh-${invoiceNumber}`}
            >
              <RefreshCcw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
            </button>
          )}
        </div>
      </button>
      {open && (
        <div className="border-t border-slate-100 px-3 py-3">
          {sorted.length === 0 ? (
            <div className="text-center text-sm text-slate-500 py-4" data-testid={`alvys-notes-empty-${invoiceNumber}`}>
              {syncedAt ? (
                <>No notes on this equipment in Alvys.</>
              ) : canRefresh ? (
                <>Not yet synced from Alvys.</>
              ) : (
                <>Waiting for next sync from Alvys.</>
              )}
              {canRefresh && (
                <div className="mt-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={doRefresh}
                    disabled={refreshing}
                    data-testid={`alvys-notes-refresh-empty-${invoiceNumber}`}
                  >
                    <RefreshCcw className={`mr-1 h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
                    {refreshing ? "Refreshing…" : "Refresh from Alvys"}
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {sorted.map((n, i) => (
                <AlvysNoteRow key={i} note={n} idx={i} invoiceNumber={invoiceNumber} />
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function AlvysNoteRow({ note, idx, invoiceNumber }) {
  const [expanded, setExpanded] = useState(false);
  const lines = (note.description || "").split("\n");
  const isLong = lines.length > 3;
  const displayed = expanded || !isLong ? lines : lines.slice(0, 3);
  const rel = useMemo(() => {
    if (!note.time) return "unknown time";
    const t = new Date(note.time).getTime();
    const ageMs = Date.now() - t;
    const minutes = Math.floor(ageMs / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    if (minutes < 1) return "just now";
    if (minutes < 60) return `${minutes} min ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 30) return `${days}d ago`;
    const months = Math.floor(days / 30);
    if (months < 12) return `${months}mo ago`;
    return `${Math.floor(months / 12)}y ago`;
  }, [note.time]);

  // Absolute stamp shown inline alongside the user name. MM/DD/YY HH:mm (24h)
  // to match the operator convention already used inside note descriptions
  // (e.g. "08/28/26 RETURNED"). Skipped entirely if `time` is null so we
  // don't render a dangling separator dot.
  const absStamp = useMemo(() => {
    if (!note.time) return null;
    const d = new Date(note.time);
    if (Number.isNaN(d.getTime())) return null;
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const yy = String(d.getFullYear()).slice(-2);
    const hh = String(d.getHours()).padStart(2, "0");
    const min = String(d.getMinutes()).padStart(2, "0");
    return `${mm}/${dd}/${yy} ${hh}:${min}`;
  }, [note.time]);

  const copyDesc = () => {
    if (!navigator.clipboard) return;
    navigator.clipboard.writeText(note.description || "");
    toast.success("Copied");
  };

  return (
    <li className="group py-2.5" data-testid={`alvys-note-${invoiceNumber}-${idx}`}>
      <div className="mb-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
        <span title={note.time ? new Date(note.time).toLocaleString() : ""} className="font-medium text-slate-700">
          {rel}
        </span>
        {note.note_type && (
          <Badge variant="secondary" className="bg-slate-100 px-1.5 py-0 text-[10px] font-normal text-slate-500">
            {note.note_type}
          </Badge>
        )}
        {note.user && <span className="text-slate-500">· {note.user}</span>}
        {absStamp && (
          <span
            className="text-slate-500"
            data-testid={`alvys-note-abstime-${invoiceNumber}-${idx}`}
          >
            · {absStamp}
          </span>
        )}
        <button
          type="button"
          onClick={copyDesc}
          className="ml-auto opacity-0 transition-opacity hover:text-slate-700 group-hover:opacity-100"
          title="Copy description"
          data-testid={`alvys-note-copy-${invoiceNumber}-${idx}`}
        >
          <Copy className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="whitespace-pre-wrap text-sm text-slate-800">
        {displayed.join("\n")}
      </div>
      {isLong && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-1 text-xs text-slate-500 hover:text-slate-700"
          data-testid={`alvys-note-toggle-${invoiceNumber}-${idx}`}
        >
          {expanded ? "Show less" : "Show more"}
        </button>
      )}
    </li>
  );
}


// ---------------------------------------------------------------------------
// Audit panel — collapsible chronological event list (admin/accounting)
// ---------------------------------------------------------------------------
function AuditPanel({ invoiceId, invoiceNumber, reloadTick }) {
  const [open, setOpen] = useState(false);
  const [events, setEvents] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true); setErr("");
    api.get(`/equipment-repairs/invoices/${invoiceId}/audit`)
      .then((r) => { if (!cancelled) setEvents(r.data.events || []); })
      .catch((e) => { if (!cancelled) setErr(extractError(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, invoiceId, reloadTick]);

  const iconFor = (type) => {
    const base = "mt-1 h-2 w-2 shrink-0 rounded-full";
    if (type === "imported")        return <span className={`${base} bg-slate-400`} />;
    if (type === "synced")          return <span className={`${base} bg-sky-500`} />;
    if (type === "driver_resolved") return <span className={`${base} bg-violet-500`} />;
    if (type === "driver_accepted_samsara") return <span className={`${base} bg-rose-500`} />;
    if (type === "charge_reversed") return <span className={`${base} bg-rose-500`} />;
    if (type === "allocated")       return <span className={`${base} bg-amber-500`} />;
    if (type === "committed")       return <span className={`${base} bg-emerald-500`} />;
    if (type === "invoice_rejected")        return <span className={`${base} bg-rose-600`} />;
    if (type === "invoice_reject_reversed") return <span className={`${base} bg-slate-500`} />;
    return <span className={`${base} bg-slate-300`} />;
  };
  const fmtAt = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return String(iso);
    return d.toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div className="mt-4 rounded-md border border-slate-200 bg-white" data-testid={`repair-audit-${invoiceNumber}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between px-4 py-2 text-left text-sm font-medium text-slate-700 hover:bg-slate-50"
        data-testid={`repair-audit-toggle-${invoiceNumber}`}
      >
        <span className="flex items-center gap-2">
          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          Audit trail
        </span>
        {events != null && <span className="text-xs text-slate-400">{events.length} event{events.length === 1 ? "" : "s"}</span>}
      </button>
      {open && (
        <div className="border-t border-slate-200 px-4 py-3">
          {loading && !events && <div className="text-sm text-slate-500">Loading…</div>}
          {err && <Alert variant="destructive"><AlertDescription>{err}</AlertDescription></Alert>}
          {!err && events && events.length === 0 && (
            <p className="text-sm text-slate-500">No audit events yet.</p>
          )}
          {!err && events && events.length > 0 && (
            <ol className="space-y-2">
              {events.map((e, i) => (
                <li key={i} className="flex gap-3 text-sm">
                  {iconFor(e.type)}
                  <div className="min-w-0 flex-1">
                    <div className="text-slate-800">{e.message}</div>
                    <div className="text-xs text-slate-400">
                      <span className="font-mono">{e.type}</span> · {fmtAt(e.at)} · {e.by}
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SamsaraMileageCell — two-line block for AT SERVICE DATE + CURRENT with Δ.
// Collapses to one line when service_date == current reading date. Handles
// trailer / null / partial-data empty states per Phase 2c spec.
// ---------------------------------------------------------------------------
function SamsaraMileageCell({ detail }) {
  const svc = detail.samsara_mileage;
  const svcSrc = detail.samsara_mileage_source;
  const svcAt = detail.samsara_mileage_reading_date;
  const cur = detail.samsara_mileage_current;
  const curSrc = detail.samsara_mileage_current_source;
  const curAt = detail.samsara_mileage_current_reading_date;
  const curFetched = detail.samsara_mileage_current_fetched_at;
  // Freshness hint on the AT SVC line only. Current-mileage is by
  // definition "now", so a stale-vs-service label there is meaningless.
  const svcGap = formatServiceGap(svcAt, detail.service_date);

  const srcLabel = (s) => (s === "obd" ? "OBD" : s === "gps_est" || s === "gps_estimated" || s === "gps" ? "GPS est." : "");

  // Both unavailable — render "Samsara not resolved" for trucks and
  // (post Phase 2c-trailer) also for trailers whose name isn't in
  // Samsara's roster or that have no telemetry for the service window.
  const hasAny = svc != null || cur != null;
  if (!hasAny) {
    return (
      <div data-testid={`samsara-mileage-${detail.invoice_number}`}>
        <div className="text-xs uppercase tracking-wide text-slate-500">Samsara Mileage</div>
        <div className="mt-0.5 text-sm text-slate-400">— (Samsara not resolved)</div>
      </div>
    );
  }

  const collapseSameDay = svc != null && cur != null && isSameLocalDay(svcAt, curAt);
  const delta = svc != null && cur != null ? Math.round(Number(cur) - Number(svc)) : null;
  const deltaHeavy = delta != null && Math.abs(delta) > 10000;

  return (
    <div data-testid={`samsara-mileage-${detail.invoice_number}`}>
      <div className="text-xs uppercase tracking-wide text-slate-500">Samsara Mileage</div>
      {collapseSameDay ? (
        <div className="mt-0.5 text-sm text-slate-900">
          {formatMileage(svc)}
          <span className="ml-2 text-xs text-slate-500">
            {srcLabel(svcSrc)} · {formatCompactDT(svcAt)}
            {svcGap && <span data-testid={`samsara-mileage-gap-${detail.invoice_number}`}> · {svcGap}</span>}
          </span>
          {detail.mileage_mismatch && (
            <MismatchAsterisk
              tooltip={`FullBay reports different mileage: ${formatMileage(detail.chassis_mileage) || "unknown"}`}
              testid={`mileage-mismatch-samsara-${detail.invoice_number}`}
            />
          )}
        </div>
      ) : (
        <>
          <div
            className="mt-0.5 text-sm text-slate-900"
            data-testid={`samsara-mileage-svc-${detail.invoice_number}`}
          >
            <span className="mr-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">At svc:</span>
            {svc != null ? (
              <>
                {formatMileage(svc)}
                <span className="ml-1 text-xs text-slate-500">
                  · {srcLabel(svcSrc)}
                  {formatCompactDT(svcAt) ? ` · ${formatCompactDT(svcAt)}` : ""}
                  {svcGap && <span data-testid={`samsara-mileage-gap-${detail.invoice_number}`}> · {svcGap}</span>}
                </span>
                {detail.mileage_mismatch && (
                  <MismatchAsterisk
                    tooltip={`FullBay reports different mileage: ${formatMileage(detail.chassis_mileage) || "unknown"}`}
                    testid={`mileage-mismatch-samsara-${detail.invoice_number}`}
                  />
                )}
              </>
            ) : (
              <span className="text-slate-400">— (no Samsara data for this date)</span>
            )}
          </div>
          <div
            className="text-sm text-slate-700"
            data-testid={`samsara-mileage-current-${detail.invoice_number}`}
          >
            <span className="mr-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">Now:</span>
            {cur != null ? (
              <>
                {formatMileage(cur)}
                <span className="ml-1 text-xs text-slate-500">
                  · {srcLabel(curSrc)}
                  {formatCompactDT(curAt) ? ` · ${formatCompactDT(curAt)}` : ""}
                </span>
                {delta != null && (
                  <span className={`ml-1 text-xs ${deltaHeavy ? "text-amber-700" : "text-slate-500"}`}>
                    · Δ {delta >= 0 ? "+" : ""}{delta.toLocaleString()} mi
                  </span>
                )}
              </>
            ) : curFetched ? (
              <span className="text-slate-400">—</span>
            ) : (
              <span className="text-slate-400">Not yet fetched</span>
            )}
          </div>
        </>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// SamsaraLocationCell — GPS position at service_date. Precedence:
// address_name (yard/shop label) → formatted_location (street) → coords +
// Google Maps link. Handles trailer / never-fetched / fetched-but-empty
// empty states per Phase 2c spec.
// ---------------------------------------------------------------------------
function SamsaraLocationCell({ detail }) {
  const loc = detail.samsara_location_at_service_date;
  const fetched = detail.samsara_location_at_service_date_fetched_at;

  if (!loc) {
    return (
      <div data-testid={`samsara-location-${detail.invoice_number}`}>
        <div className="text-xs uppercase tracking-wide text-slate-500">Location at Service Date</div>
        {fetched ? (
          <>
            <div className="mt-0.5 text-sm text-slate-400">—</div>
            <div className="text-xs text-slate-400">No GPS data for this date</div>
          </>
        ) : (
          <div className="mt-0.5 text-sm text-slate-400">Not yet fetched</div>
        )}
      </div>
    );
  }

  const { latitude, longitude, address_name, formatted_location, reading_date } = loc;
  let primary;
  if (address_name) {
    primary = <span className="text-slate-900">{address_name}</span>;
  } else if (formatted_location) {
    primary = <span className="text-slate-900">{formatted_location}</span>;
  } else if (latitude != null && longitude != null) {
    const url = `https://www.google.com/maps?q=${latitude},${longitude}`;
    primary = (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-sky-700 underline hover:text-sky-900"
      >
        {Number(latitude).toFixed(4)}, {Number(longitude).toFixed(4)}
      </a>
    );
  } else {
    primary = <span className="text-slate-400">—</span>;
  }
  return (
    <div data-testid={`samsara-location-${detail.invoice_number}`}>
      <div className="text-xs uppercase tracking-wide text-slate-500">Location at Service Date</div>
      <div className="mt-0.5 text-sm">{primary}</div>
      {reading_date && (
        <div className="text-xs text-slate-500">
          Samsara GPS · {formatCompactDT(reading_date)}
          {(() => {
            const gap = formatServiceGap(reading_date, detail.service_date);
            return gap ? (
              <span data-testid={`samsara-location-gap-${detail.invoice_number}`}> · {gap}</span>
            ) : null;
          })()}
        </div>
      )}
    </div>
  );
}


function MetaCell({ label, value, mono, hint, decorator }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`mt-0.5 flex items-center gap-1 text-sm text-slate-900 ${mono ? "font-mono" : ""}`}>
        <span>{value || <span className="text-slate-400">—</span>}</span>
        {decorator}
      </div>
      {hint && <div className="text-xs text-slate-400">{hint}</div>}
    </div>
  );
}

// Small red asterisk to flag a field involved in a mismatch, with tooltip.
function MismatchAsterisk({ tooltip, testid }) {
  return (
    <span
      className="cursor-help text-sm font-semibold text-rose-600"
      title={tooltip}
      data-testid={testid}
      aria-label={tooltip}
    >
      *
    </span>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function EquipmentRepairs() {
  const { user } = useAuth();
  const isPrivileged = user?.role === "admin" || user?.role === "accounting";
  const isAdmin = user?.role === "admin";

  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [status, setStatus] = useState("");
  const [unitNumber, setUnitNumber] = useState("");
  const [driverId, setDriverId] = useState(null);
  const [driverName, setDriverName] = useState("");
  const [unitType, setUnitType] = useState("");
  const [hasRepeats, setHasRepeats] = useState("");        // "", "true", "false"
  const [warranty, setWarranty] = useState("");             // "", "true"
  const [mismatch, setMismatch] = useState("");             // "", "driver", "mileage"
  const [fullbayPaymentStatus, setFullbayPaymentStatus] = useState("");  // "", "paid", "unpaid", "partial"
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [items, setItems] = useState([]);
  const [expanded, setExpanded] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [syncOpen, setSyncOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  const exportCsv = useCallback(async () => {
    setExporting(true);
    try {
      const params = {};
      if (companyId) params.company_id = companyId;
      if (status) params.status = status;
      if (unitNumber) params.unit_number = unitNumber;
      if (driverId) params.driver_id = driverId;
      if (unitType) params.unit_type = unitType;
      if (hasRepeats) params.has_repeats = hasRepeats;
      if (warranty) params.possible_warranty = warranty;
      if (mismatch === "driver") params.driver_mismatch = true;
      if (mismatch === "mileage") params.mileage_mismatch = true;
      if (fullbayPaymentStatus) params.fullbay_payment_status = fullbayPaymentStatus;
      if (dateFrom) params.invoice_date_from = dateFrom;
      if (dateTo) params.invoice_date_to = dateTo;
      if (debouncedQ) params.q = debouncedQ;
      const resp = await api.get("/equipment-repairs/export.csv", {
        params, responseType: "blob", timeout: 120000,
      });
      const blob = new Blob([resp.data], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const today = new Date().toISOString().slice(0, 10);
      a.download = `equipment-repairs-${today}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("CSV downloaded");
    } catch (e) { toast.error(extractError(e)); }
    finally { setExporting(false); }
  }, [companyId, status, unitNumber, driverId, unitType, hasRepeats, warranty, mismatch, fullbayPaymentStatus, dateFrom, dateTo, debouncedQ]);

  useEffect(() => { const t = setTimeout(() => setDebouncedQ(q), 350); return () => clearTimeout(t); }, [q]);
  useEffect(() => { setPage(1); }, [companyId, status, unitNumber, driverId, unitType, hasRepeats, warranty, mismatch, fullbayPaymentStatus, dateFrom, dateTo, debouncedQ]);

  // Load companies once (admins + accounting only — used for filter dropdown + sync)
  useEffect(() => {
    if (!isPrivileged) return;
    api.get("/companies", { params: { active_only: false } })
      .then((r) => setCompanies(r.data))
      .catch(() => setCompanies([]));
  }, [isPrivileged]);

  // See scroll-preservation notes below (useLayoutEffect on items.length).
  const lastScrollY = useRef(0);

  const load = useCallback(async () => {
    // Snapshot scroll position BEFORE the refetch fires — but only if
    // meaningful. A second load() firing back-to-back (e.g., because both
    // `onChange` and the `repairs:invoice-reload` event triggered) would
    // otherwise clobber the snapshot to 0 after the first load already reset
    // scroll, defeating the restore in the useLayoutEffect below.
    if (window.scrollY > 0) lastScrollY.current = window.scrollY;
    setItems((cur) => { if (cur.length === 0) setLoading(true); return cur; });
    setErr("");
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (companyId) params.company_id = companyId;
      if (status) params.status = status;
      if (unitNumber) params.unit_number = unitNumber;
      if (driverId) params.driver_id = driverId;
      if (unitType) params.unit_type = unitType;
      if (hasRepeats) params.has_repeats = hasRepeats;
      if (warranty) params.possible_warranty = warranty;
      if (mismatch === "driver") params.driver_mismatch = true;
      if (mismatch === "mileage") params.mileage_mismatch = true;
      if (fullbayPaymentStatus) params.fullbay_payment_status = fullbayPaymentStatus;
      if (dateFrom) params.invoice_date_from = dateFrom;
      if (dateTo) params.invoice_date_to = dateTo;
      if (debouncedQ) params.q = debouncedQ;
      const { data } = await api.get("/equipment-repairs/invoices", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
      // Restore scroll after the DOM shrinks. Two `requestAnimationFrame`s
      // ensure we run after React has flushed the state update AND the
      // browser has performed its own scroll adjustment (which can reset to
      // 0 when the anchor element is removed). At that point our restored
      // `lastScrollY` reflects the user's intent from before the mutation.
      requestAnimationFrame(() => requestAnimationFrame(() => {
        const maxScroll = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
        const remembered = lastScrollY.current;
        if (window.scrollY === 0 && remembered > 0 && maxScroll > 0) {
          window.scrollTo({ top: Math.min(remembered, maxScroll), behavior: "instant" });
        } else if (window.scrollY > maxScroll) {
          window.scrollTo({ top: maxScroll, behavior: "instant" });
        }
      }));
    } catch (e) { setErr(extractError(e)); } finally { setLoading(false); }
  }, [page, companyId, status, unitNumber, driverId, unitType, hasRepeats, warranty, mismatch, fullbayPaymentStatus, dateFrom, dateTo, debouncedQ]);

  useEffect(() => { load(); }, [load]);

  const toggleExpand = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const companyLabel = useMemo(() => {
    if (!companyId) return null;
    const c = companies.find((x) => x.id === companyId);
    return c ? c.code : null;
  }, [companyId, companies]);

  const navigateToInvoice = (invoiceId, invoiceNumber) => {
    setExpanded((prev) => new Set([...prev, invoiceId]));
    // If the invoice isn't in the current page, alert the user softly.
    if (!items.some((x) => x.id === invoiceId)) {
      toast.info(`Invoice #${invoiceNumber} isn't on this page — try clearing filters or searching for it.`);
      return;
    }
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="repairs-row-${invoiceNumber}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  return (
    <RepeatHistoryProvider onNavigateToInvoice={navigateToInvoice}>
    <div data-testid="equipment-repairs-page">
      <div className="mb-6 flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 sm:text-3xl">Equipment Repairs</h1>
          <p className="mt-1 text-sm text-slate-500">
            Imported FullBay repair invoices with Alvys driver suggestions.
          </p>
        </div>
        {isPrivileged && (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={exportCsv}
              disabled={exporting}
              data-testid="repairs-export-csv"
            >
              <Download className="mr-2 h-4 w-4" />
              {exporting ? "Exporting…" : "Export CSV"}
            </Button>
            <Button
              onClick={() => setSyncOpen(true)}
              className="bg-[#0F1E3D] text-white hover:bg-[#0F1E3D]/90"
              data-testid="repairs-sync-button"
            >
              <RefreshCcw className="mr-2 h-4 w-4" /> Sync FullBay
            </Button>
          </div>
        )}
      </div>

      {/* Filter bar */}
      <div className="mb-4 space-y-3 rounded-md border border-slate-200 bg-white p-4">
        {/* Status pills */}
        <div className="flex flex-wrap items-center gap-2" data-testid="repairs-filter-status">
          <span className="mr-1 text-xs font-medium uppercase tracking-wide text-slate-500">Status</span>
          {STATUSES.map((s) => (
            <button
              key={s.value || "all"}
              onClick={() => setStatus(s.value)}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                status === s.value ? "bg-[#0F1E3D] text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}
              data-testid={`repairs-filter-status-${s.value || "all"}`}
            >
              {s.label}
            </button>
          ))}
        </div>

        {/* Unit type pills */}
        <div className="flex flex-wrap items-center gap-2" data-testid="repairs-filter-unit-type">
          <span className="mr-1 text-xs font-medium uppercase tracking-wide text-slate-500">Type</span>
          {UNIT_TYPES.map((t) => (
            <button
              key={t.value || "all"}
              onClick={() => setUnitType(t.value)}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                unitType === t.value ? "bg-[#0F1E3D] text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}
              data-testid={`repairs-filter-unit-type-${t.value || "all"}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Repeats + Warranty pills */}
        <div className="flex flex-wrap items-center gap-2" data-testid="repairs-filter-has-repeats">
          <span className="mr-1 text-xs font-medium uppercase tracking-wide text-slate-500">Repeats</span>
          {REPEATS.map((r) => (
            <button
              key={r.value || "all"}
              onClick={() => setHasRepeats(r.value)}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                hasRepeats === r.value ? "bg-[#0F1E3D] text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}
              data-testid={`repairs-filter-has-repeats-${r.value || "all"}`}
            >
              {r.label}
            </button>
          ))}
          {hasRepeats !== "false" && (
            <div className="ml-2 flex flex-wrap items-center gap-2" data-testid="repairs-filter-warranty">
              <span className="mr-1 text-xs font-medium uppercase tracking-wide text-slate-500">Warranty</span>
              {WARRANTY.map((w) => (
                <button
                  key={w.value || "all"}
                  onClick={() => setWarranty(w.value)}
                  className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                    warranty === w.value
                      ? "bg-rose-600 text-white"
                      : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                  }`}
                  data-testid={`repairs-filter-warranty-${w.value || "all"}`}
                >
                  {w.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Mismatches filter (Samsara vs Alvys/FullBay) */}
        <div className="flex flex-wrap items-center gap-2" data-testid="repairs-filter-mismatch">
          <span className="mr-1 text-xs font-medium uppercase tracking-wide text-slate-500">Mismatches</span>
          {[
            { value: "", label: "All" },
            { value: "driver", label: "Driver mismatch" },
            { value: "mileage", label: "Mileage mismatch" },
          ].map((m) => (
            <button
              key={m.value || "all"}
              onClick={() => setMismatch(m.value)}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                mismatch === m.value
                  ? "bg-rose-600 text-white"
                  : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}
              data-testid={`repairs-filter-mismatch-${m.value || "all"}`}
            >
              {m.label}
            </button>
          ))}
        </div>

        {/* FullBay payment status filter — derived from raw_payload.balance vs total */}
        <div className="flex flex-wrap items-center gap-2" data-testid="repairs-filter-fullbay-payment">
          <span className="mr-1 text-xs font-medium uppercase tracking-wide text-slate-500">FullBay Payment</span>
          {FULLBAY_PAYMENT_STATUSES.map((p) => (
            <button
              key={p.value || "all"}
              onClick={() => setFullbayPaymentStatus(p.value)}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                fullbayPaymentStatus === p.value
                  ? "bg-[#0F1E3D] text-white"
                  : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}
              data-testid={`repairs-filter-fullbay-payment-${p.value || "all"}`}
            >
              {p.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-6">
          {isPrivileged && (
            <div className="md:col-span-2">
              <Label className="text-xs text-slate-500">Company</Label>
              <Select value={companyId || "__all__"} onValueChange={(v) => setCompanyId(v === "__all__" ? "" : v)}>
                <SelectTrigger className="mt-1 h-9" data-testid="repairs-filter-company"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All companies</SelectItem>
                  {companies.filter((c) => c.active).map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.code} — {c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="text-xs text-slate-500">Unit #</Label>
            <Input value={unitNumber} onChange={(e) => setUnitNumber(e.target.value.trim())} placeholder="e.g. 8264" className="mt-1 h-9" data-testid="repairs-filter-unit" />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Driver</Label>
            <div className="mt-1">
              <DriverPicker
                value={driverId} name={driverName}
                onChange={(id, name) => { setDriverId(id); setDriverName(name || ""); }}
              />
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-500">From</Label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="mt-1 h-9" data-testid="repairs-filter-date-from" />
          </div>
          <div>
            <Label className="text-xs text-slate-500">To</Label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="mt-1 h-9" data-testid="repairs-filter-date-to" />
          </div>
        </div>
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search invoice #, unit, or VIN…" className="pl-9" data-testid="repairs-filter-q" />
        </div>
      </div>

      {err && <Alert variant="destructive" className="mb-4"><AlertDescription>{err}</AlertDescription></Alert>}

      {/* Table */}
      <div className="rounded-md border border-slate-200 bg-white">
        <Table data-testid="repairs-table" className="table-fixed">
          <TableHeader>
            <TableRow>
              <TableHead className="w-8" />
              <TableHead className="w-[9%]">Invoice #</TableHead>
              <TableHead className="w-[10%]">Date</TableHead>
              <TableHead className="w-[9%] pr-6 text-right">Total</TableHead>
              <TableHead className="w-[17%]">Unit</TableHead>
              <TableHead className="w-[22%]">Suggested Driver</TableHead>
              <TableHead className="w-[8%] text-right">Corrections</TableHead>
              <TableHead className="w-[13%]">Repeats</TableHead>
              <TableHead className="w-[10%]">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && items.length === 0 ? (
              <TableRow><TableCell colSpan={9} className="py-10 text-center text-slate-500">Loading…</TableCell></TableRow>
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="py-10 text-center text-slate-500" data-testid="repairs-empty">
                  {total === 0 && !companyId && !status && !unitNumber && !driverId && !q ? (
                    <>No repair invoices yet. {isPrivileged && "Click “Sync FullBay” to import."}</>
                  ) : "No invoices match those filters."}
                </TableCell>
              </TableRow>
            ) : items.map((it) => {
              const open = expanded.has(it.id);
              return (
                <FragmentWithKey key={it.id}>
                  <TableRow data-testid={`repairs-row-${it.invoice_number}`}>
                    <TableCell>
                      <button
                        onClick={() => toggleExpand(it.id)}
                        className="rounded p-1 text-slate-500 hover:bg-slate-100"
                        data-testid={`repairs-expand-${it.invoice_number}`}
                        aria-label={open ? "Collapse" : "Expand"}
                      >
                        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </button>
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      <span className="inline-flex items-center gap-2">
                        <span>{it.invoice_number || "—"}</span>
                        <PaymentStatusPill value={it.fullbay_payment_status} invoiceNumber={it.invoice_number} />
                      </span>
                    </TableCell>
                    <TableCell className="text-sm">{formatDate(it.invoice_date)}</TableCell>
                    <TableCell className="pr-6 text-right text-sm font-medium tabular-nums">{formatMoney(it.total)}</TableCell>
                    <TableCell className="text-sm">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="font-mono">{it.unit_number || "—"}</span>
                        <UnitTypeBadge value={it.unit_type} invoiceNumber={it.invoice_number} subType={it.unit_subtype} />
                        <EquipmentStatusPill
                          value={it.equipment_status}
                          rawValue={it.equipment_status_raw}
                          invoiceNumber={it.invoice_number}
                        />
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">
                      <div className="truncate">
                        <span>{it.suggested_driver_name || <span className="text-slate-400">—</span>}</span>
                        {it.driver_mismatch && (
                          <AlertTriangle
                            className="ml-1 inline-block h-3.5 w-3.5 text-rose-600"
                            title={`Samsara reports different driver: ${it.samsara_driver_name || "unknown"}`}
                            data-testid={`driver-mismatch-${it.invoice_number}`}
                          />
                        )}
                      </div>
                      {it.driver_resolution_reason && !it.last_alvys_driver_id && (
                        <div className="truncate text-xs text-slate-400">via {it.driver_resolution_reason}</div>
                      )}
                      {!it.suggested_driver_id && it.last_alvys_driver_id && (
                        <div
                          className="truncate text-xs text-slate-400"
                          title={it.last_alvys_driver_name || ""}
                          data-testid={`last-alvys-driver-${it.invoice_number}`}
                        >
                          (last: {it.last_alvys_driver_name || "unknown"}
                          {it.driver_still_assigned ? " · still assigned" : ""}
                          {formatMMMD(it.last_alvys_driver_end_date) ? ` · last trip ${formatMMMD(it.last_alvys_driver_end_date)}` : ""})
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-right text-sm tabular-nums">{it.corrections_count}</TableCell>
                    <TableCell className="text-sm">
                      <RepeatRowPill invoice={it} />
                    </TableCell>
                    <TableCell className="text-sm">
                      <Badge className={statusBadge(it.status)} data-testid={`repairs-status-${it.invoice_number}`}>
                        {it.status === "rejected" ? "REJECTED" : it.status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                  {open && (
                    <TableRow>
                      <TableCell colSpan={9} className="p-0">
                        <InvoiceReceipt
                          invoiceId={it.id}
                          canEdit={isPrivileged}
                          isAdmin={isAdmin}
                          onInvoiceChanged={load}
                          invoiceMeta={it}
                        />
                      </TableCell>
                    </TableRow>
                  )}
                </FragmentWithKey>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {total > 0 && (
        <div className="mt-4 flex items-center justify-between text-sm text-slate-600" data-testid="repairs-pagination">
          <div>
            {Math.min((page - 1) * PAGE_SIZE + 1, total)}–{Math.min(page * PAGE_SIZE, total)} of {total}
            {companyLabel && ` · ${companyLabel}`}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} data-testid="repairs-page-prev">Prev</Button>
            <span className="text-xs">Page {page} / {totalPages}</span>
            <Button variant="ghost" size="sm" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages} data-testid="repairs-page-next">Next</Button>
          </div>
        </div>
      )}

      {isPrivileged && (
        <SyncFullBayDialog
          companies={companies}
          open={syncOpen}
          onOpenChange={setSyncOpen}
          onComplete={load}
        />
      )}
    </div>
    </RepeatHistoryProvider>
  );
}
